"use client"

import { useEffect, useState } from "react"
import Icon from "./Icon"
import BankCombobox from "./BankCombobox"
import api from "../utils/api"
import { generateUploadButton } from "@uploadthing/react"
import "@uploadthing/react/styles.css"
const UploadButton = generateUploadButton({ url: `${process.env.REACT_APP_API_BASE || 'http://localhost:5000'}/api/uploadthing` })

function ProfileModal({
  user,
  onClose,
  onSaved,
  banksCatalog: banksCatalogProp = []
}) {
  const isSuperAdmin = String(user?.role || '') === 'A'
  const [editMode, setEditMode] = useState(false)
  const [formError, setFormError] = useState('')
  const [formLoading, setFormLoading] = useState(false)
  const [banksCatalog, setBanksCatalog] = useState([])

  useEffect(() => {
    setBanksCatalog(Array.isArray(banksCatalogProp) ? banksCatalogProp : [])
  }, [banksCatalogProp])

  useEffect(() => {
    if (banksCatalog && banksCatalog.length > 0) return
    let mounted = true
    const loadBanks = async () => {
      try {
        let response = await fetch('/bankslist.json')
        if (response.ok) {
          const ct = (response.headers.get('content-type') || '').toLowerCase()
          if (ct.includes('application/json')) {
            const data = await response.json()
            const list = Array.isArray(data) ? data.filter(b => b && b.name) : []
            if (mounted) setBanksCatalog(list)
            return
          }
        }
        // Fallback: parse xlsx if present and xlsx lib is available
        try {
          const XLSX = await import('xlsx')
          response = await fetch('/bankslist.xlsx')
          if (response.ok) {
            const arrayBuffer = await response.arrayBuffer()
            const wb = XLSX.read(arrayBuffer, { type: 'array' })
            const ws = wb.Sheets[wb.SheetNames[0]]
            const json = XLSX.utils.sheet_to_json(ws, { header: 1 })
            const rows = Array.isArray(json) ? json : []
            const header = rows[0] || []
            const nameIdx = header.findIndex(h => String(h).toLowerCase() === 'name')
            const paybillIdx = header.findIndex(h => String(h).toLowerCase() === 'paybill')
            const list = rows
              .slice(1)
              .map(r => ({ name: r[nameIdx] || '', paybill: r[paybillIdx] || '' }))
              .filter(b => b.name)
            if (mounted) setBanksCatalog(list)
          }
        } catch (e) {
          console.warn('XLSX not available; skipping banks xlsx parsing')
        }
      } catch (e) {
        console.warn('Failed to load banks catalog', e)
      }
    }
    loadBanks()
    return () => { mounted = false }
  }, [banksCatalog])

  const [profileForm, setProfileForm] = useState({
    name: '',
    idNumber: '',
    email: '',
    phone: '',
    bankName: '',
    bankAccNumber: '',
    paybill: '',
    currentPin: '',
    newPin: '',
    confirmPin: '',
    profilePhoto: null
  })

  useEffect(() => {
    setProfileForm({
      name: user?.name || '',
      idNumber: user?.idNumber || '',
      email: user?.email || '',
      phone: user?.phone || '',
      bankName: user?.bankName || '',
      bankAccNumber: user?.bankAccNumber || '',
      paybill: user?.paybill || '',
      currentPin: '',
      newPin: '',
      confirmPin: '',
      profilePhoto: user?.profilePhoto || null
    })
  }, [user])

  const handleProfileSave = async (e) => {
    e.preventDefault()
    setFormLoading(true)
    setFormError('')

    if (profileForm.newPin) {
      if (!profileForm.currentPin) {
        setFormError('Current PIN is required to change PIN')
        setFormLoading(false)
        return
      }
      if (profileForm.newPin !== profileForm.confirmPin) {
        setFormError('New PIN and confirmation do not match')
        setFormLoading(false)
        return
      }
      if (!/^\d{4}$/.test(profileForm.newPin)) {
        setFormError('New PIN must be exactly 4 digits')
        setFormLoading(false)
        return
      }
    }

    try {
      const updateData = {
        name: profileForm.name,
        idNumber: profileForm.idNumber,
        email: profileForm.email,
        phone: profileForm.phone,
        bankName: profileForm.bankName,
        bankAccNumber: profileForm.bankAccNumber,
        paybill: profileForm.paybill,
        profilePhoto: profileForm.profilePhoto && profileForm.profilePhoto.url ? profileForm.profilePhoto : undefined
      }

      if (profileForm.newPin) {
        updateData.currentPin = profileForm.currentPin
        updateData.newPin = profileForm.newPin
      }

      const response = await api.put('/auth/profile', updateData)
      const updatedUser = response.data.user
      onSaved?.(updatedUser)
      setEditMode(false)
      setFormError('')
      setProfileForm({
        name: updatedUser?.name || '',
        idNumber: updatedUser?.idNumber || '',
        email: updatedUser?.email || '',
        phone: updatedUser?.phone || '',
        bankName: updatedUser?.bankName || '',
        bankAccNumber: updatedUser?.bankAccNumber || '',
        paybill: updatedUser?.paybill || '',
        currentPin: '',
        newPin: '',
        confirmPin: '',
        profilePhoto: updatedUser?.profilePhoto || null
      })
      alert('Profile updated successfully!')
    } catch (error) {
      setFormError('Error updating profile: ' + (error.response?.data?.error || error.message))
    } finally {
      setFormLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[80] p-4">
      <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Profile Information</h3>
            <button
              onClick={() => {
                onClose?.()
                setEditMode(false)
                setFormError('')
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              <Icon name="x" size="1.5rem" />
            </button>
          </div>

          {formError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
              <p className="text-red-700 text-sm">{formError}</p>
            </div>
          )}

          {!editMode ? (
            <div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
                <div className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-700">{user?.name || 'Not set'}</div>
              </div>
              <div className="mb-4 flex items-center gap-3">
                <div className="w-16 h-16 rounded-full overflow-hidden border">
                  <img
                    src={user?.profilePhoto?.url || 'https://via.placeholder.com/64'}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="text-sm text-gray-600">Profile Photo</div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <div className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-700">{user?.email || 'Not set'}</div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                <div className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-700">{user?.phone || 'Not set'}</div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">ID Number</label>
                <div className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-700">{user?.idNumber || 'Not set'}</div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Bank Name</label>
                <div className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-700">{user?.bankName || 'Not set'}</div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Paybill</label>
                <div className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-700">{user?.paybill || 'Not set'}</div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Bank Account Number</label>
                <div className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-700">{user?.bankAccNumber || 'Not set'}</div>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setEditMode(true)}
                  className="flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 transition-colors"
                >
                  Edit Profile
                </button>
                <button
                  type="button"
                  onClick={() => {
                    onClose?.()
                    setEditMode(false)
                    setFormError('')
                  }}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleProfileSave}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Profile Photo</label>
                <div className="flex items-center gap-3">
                  <div className="w-16 h-16 rounded-full overflow-hidden border">
                    <img
                      src={profileForm.profilePhoto?.url || 'https://via.placeholder.com/64'}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <UploadButton
                    endpoint="profilePhoto"
                    onClientUploadComplete={(res) => {
                      const f = (res && res[0]) || null
                      if (f) {
                        setProfileForm(prev => ({
                          ...prev,
                          profilePhoto: {
                            url: f.url,
                            key: f.key,
                            name: `profile-${user?._id || 'me'}-${Date.now()}-${(f.name || '').replace(/\s+/g, '-')}`
                          }
                        }))
                      }
                    }}
                    onUploadError={(err) => {
                      console.error('Profile upload error', err)
                      alert('Failed to upload profile photo')
                    }}
                  />
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
                <input
                  type="text"
                  value={profileForm.name}
                  onChange={isSuperAdmin ? ((e) => setProfileForm({...profileForm, name: e.target.value})) : undefined}
                  disabled={!isSuperAdmin}
                  className={`w-full px-3 py-2 rounded-lg ${isSuperAdmin ? 'border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500' : 'border border-gray-200 bg-gray-50 text-gray-700 cursor-not-allowed'}`}
                  required
                />
                {!isSuperAdmin && (
                  <p className="mt-1 text-xs text-gray-500">Only super admins can change the profile name.</p>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  value={profileForm.email}
                  onChange={(e) => setProfileForm({...profileForm, email: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  required
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                <input
                  type="tel"
                  value={profileForm.phone}
                  onChange={(e) => setProfileForm({...profileForm, phone: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">ID Number</label>
                <input
                  type="text"
                  value={profileForm.idNumber}
                  onChange={isSuperAdmin ? ((e) => setProfileForm({...profileForm, idNumber: e.target.value})) : undefined}
                  disabled={!isSuperAdmin}
                  className={`w-full px-3 py-2 rounded-lg ${isSuperAdmin ? 'border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500' : 'border border-gray-200 bg-gray-50 text-gray-700 cursor-not-allowed'}`}
                />
                {!isSuperAdmin && (
                  <p className="mt-1 text-xs text-gray-500">Only super admins can change the ID number.</p>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Bank Name</label>
                <BankCombobox
                  value={profileForm.bankName}
                  banksCatalog={banksCatalog}
                  onChange={(name) => setProfileForm(prev => ({ ...prev, bankName: name }))}
                  onSelectBank={(bank) => {
                    if (!bank) return
                    setProfileForm(prev => ({ ...prev, bankName: bank.name || '', paybill: bank.paybill || prev.paybill }))
                  }}
                  placeholder="Select bank or type"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Paybill</label>
                <input
                  type="text"
                  value={profileForm.paybill}
                  disabled
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-700 cursor-not-allowed"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Bank Account Number</label>
                <input
                  type="text"
                  value={profileForm.bankAccNumber}
                  onChange={(e) => setProfileForm({...profileForm, bankAccNumber: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>

              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Change PIN (Optional)</h4>

                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Current PIN</label>
                  <input
                    type="password"
                    value={profileForm.currentPin}
                    onChange={(e) => setProfileForm({...profileForm, currentPin: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Enter current 4-digit PIN"
                    maxLength="4"
                  />
                </div>

                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">New PIN</label>
                  <input
                    type="password"
                    value={profileForm.newPin}
                    onChange={(e) => setProfileForm({...profileForm, newPin: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Enter new 4-digit PIN"
                    maxLength="4"
                  />
                </div>

                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Confirm New PIN</label>
                  <input
                    type="password"
                    value={profileForm.confirmPin}
                    onChange={(e) => setProfileForm({...profileForm, confirmPin: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Confirm new 4-digit PIN"
                    maxLength="4"
                  />
                </div>

                <p className="text-xs text-gray-500">Leave these fields empty if you don't want to change your PIN</p>
              </div>

              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={formLoading}
                  className="flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                  {formLoading ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEditMode(false)
                    setFormError('')
                    setProfileForm({
                      name: user?.name || '',
                      idNumber: user?.idNumber || '',
                      email: user?.email || '',
                      phone: user?.phone || '',
                      bankName: user?.bankName || '',
                      bankAccNumber: user?.bankAccNumber || '',
                      paybill: user?.paybill || '',
                      currentPin: '',
                      newPin: '',
                      confirmPin: '',
                      profilePhoto: user?.profilePhoto || null
                    })
                  }}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}

export default ProfileModal


